import React from "react";
import { Routes } from "react-router-dom";

import { Route } from "src/shared/components";
import { LEGACY_MENTORSHIP_ENABLED } from "src/shared/constants/featureFlags";
import { HackerRoute, Route as RouteName } from "src/shared/constants/route";
import {
  Permission,
  MentorshipRequestContextProvider,
  NetworkingContextProvider,
} from "src/shared/contexts";
import NotFound from "src/views/404";

import { FAQ as HackerFAQ } from "./faq";
import { GettingStarted as HackerGettingStarted } from "./getting-started";
import { Judging as HackerJudging } from "./judging";
import { JudgingFAQ as HackerJudgingFAQ } from "./judging/faq";
import { JudgingInfo as HackerJudgingInfo } from "./judging/judging-info";
import { Museum as HackerJudgingMuseum } from "./judging/museum";
import { Prizehub as HackerJudgingPrizehub } from "./judging/prizehub";
import HackerMentorCafe from "./mentor-cafe";
import HackerMentorshipHome from "./mentorship";
import HackerRsvpBadge from "./rsvp/badge";
import HackerRsvpCareer from "./rsvp/career";
import { CareerSponsorContextProvider } from "./rsvp/career/CareerSponsorContext";
import HackerRsvpConfirmation from "./rsvp/confirmation";
import HackerRsvpEvent from "./rsvp/event";
import HackerRsvpGraphic from "./rsvp/graphic";
import HackerRsvpHome from "./rsvp/home";
import HackerRsvpPersonal from "./rsvp/personal";
import HackerRsvpProfile from "./rsvp/profile";
import HackerRsvpTravel from "./rsvp/travel";
import HackerTravelForm from "./rsvp/travel-form";
import HackerTravelSupport from "./travel-support";
import WelcomeBooklet from "./welcome-booklet";

const rsvpFormGateProps = {
  permissionDeniedTitle: "RSVP closed",
  permissionDeniedMessage:
    "The RSVP form is currently closed. You can still view your RSVP status and event information.",
  permissionDeniedActionLabel: "Back to RSVP",
  permissionDeniedRedirectTo: RouteName.HACKER_RSVP,
  permissionDeniedFor: Permission.RSVP_FORM,
};

export const HackerRoutes = () => (
  <Routes>
    <Route
      path={HackerRoute.WELCOME_BOOKLET}
      element={<WelcomeBooklet />}
      requirePermissions={[Permission.ATTENDEE_EVENT_ACCESS]}
    />
    <Route
      path={HackerRoute.RSVP}
      element={<HackerRsvpHome />}
      requirePermissions={[Permission.RSVP]}
    />
    <Route
      path={HackerRoute.TRAVEL_SUPPORT}
      element={<HackerTravelSupport />}
      requireAuth
      requireTravelSupportBot
    />
    <Route
      path={HackerRoute.PERSONAL}
      element={<HackerRsvpPersonal />}
      requirePermissions={[Permission.RSVP_EDIT, Permission.RSVP_FORM]}
      {...rsvpFormGateProps}
    />
    <Route
      path={HackerRoute.TRAVEL}
      element={<HackerRsvpTravel />}
      requirePermissions={[
        Permission.HACKER_RSVP_NOT_RESTRICTED,
        Permission.RSVP_EDIT,
        Permission.RSVP_FORM,
      ]}
      {...rsvpFormGateProps}
    />
    <Route
      path={HackerRoute.EVENT}
      element={<HackerRsvpEvent />}
      requirePermissions={[
        Permission.HACKER_RSVP_NOT_RESTRICTED,
        Permission.RSVP_EDIT,
        Permission.RSVP_FORM,
      ]}
      {...rsvpFormGateProps}
    />
    <Route
      path={HackerRoute.BADGE}
      element={
        <NetworkingContextProvider>
          <HackerRsvpBadge />
        </NetworkingContextProvider>
      }
      requirePermissions={[Permission.RSVP_EDIT, Permission.RSVP_FORM]}
      {...rsvpFormGateProps}
    />
    <Route
      path={HackerRoute.CAREER}
      element={
        <CareerSponsorContextProvider>
          <HackerRsvpCareer />
        </CareerSponsorContextProvider>
      }
      requirePermissions={[
        Permission.HACKER_RSVP_NOT_RESTRICTED,
        Permission.RSVP_EDIT,
        Permission.RSVP_FORM,
      ]}
      {...rsvpFormGateProps}
    />
    <Route
      path={HackerRoute.PROFILE}
      element={<HackerRsvpProfile />}
      requirePermissions={[
        Permission.HACKER_RSVP_NOT_RESTRICTED,
        Permission.RSVP_EDIT,
        Permission.RSVP_FORM,
      ]}
      {...rsvpFormGateProps}
    />
    <Route
      path={HackerRoute.CONFIRMATION}
      element={<HackerRsvpConfirmation />}
      requirePermissions={[Permission.RSVP_EDIT, Permission.RSVP_FORM]}
      {...rsvpFormGateProps}
    />
    <Route
      path={HackerRoute.TRAVEL_DETAILS}
      element={<HackerTravelForm />}
      requirePermissions={[Permission.HACKER_RSVP_NOT_RESTRICTED]}
    />
    <Route
      path={HackerRoute.GRAPHIC}
      element={<HackerRsvpGraphic />}
      requirePermissions={[Permission.RSVP]}
    />
    {LEGACY_MENTORSHIP_ENABLED && (
      <Route
        path={HackerRoute.MENTORSHIP}
        element={
          <MentorshipRequestContextProvider>
            <HackerMentorshipHome />
          </MentorshipRequestContextProvider>
        }
        requirePermissions={[Permission.HACKER_EVENT_ACCESS]}
        requireHackerPostRsvpTools
      />
    )}
    <Route
      path={HackerRoute.MENTOR_CAFE}
      element={<HackerMentorCafe />}
      requirePermissions={[
        Permission.HACKER_EVENT_ACCESS,
        Permission.ORGANIZER,
      ]}
      requireAll={false}
      requireHackerPostRsvpTools
    />
    <Route
      path={HackerRoute.GETTING_STARTED}
      element={<HackerGettingStarted />}
      requireHackerPostRsvpTools
    />
    <Route
      path={HackerRoute.FAQ}
      element={<HackerFAQ />}
      requirePermissions={[Permission.HACKER_EVENT_ACCESS]}
      requireHackerPostRsvpTools
    />
    <Route
      path={HackerRoute.JUDGING}
      element={<HackerJudging />}
      requirePermissions={[Permission.HACKER_EVENT_ACCESS]}
      requireJudgingOpen
    />
    <Route
      path={HackerRoute.JUDGING_INFO}
      element={<HackerJudgingInfo />}
      requirePermissions={[Permission.HACKER_EVENT_ACCESS]}
      requireJudgingOpen
    />
    <Route
      path={HackerRoute.JUDGING_MUSEUM}
      element={<HackerJudgingMuseum />}
      requirePermissions={[Permission.HACKER_EVENT_ACCESS]}
      requireJudgingOpen
    />
    <Route
      path={HackerRoute.JUDGING_PRIZEHUB}
      element={<HackerJudgingPrizehub />}
      requirePermissions={[Permission.HACKER_EVENT_ACCESS]}
      requireJudgingOpen
    />
    <Route
      path={HackerRoute.JUDGING_FAQ}
      element={<HackerJudgingFAQ />}
      requirePermissions={[Permission.HACKER_EVENT_ACCESS]}
      requireJudgingOpen
    />
    <Route path="*" element={<NotFound />} />
  </Routes>
);
